/**
 * Tutorial State Management
 *
 * Manages the 4-step onboarding tutorial state.
 * Persists completion status to localStorage.
 */

import { ref, computed } from 'vue';

// What elements to highlight for each step
export type TutorialHighlight = 'circles' | 'sequencer' | 'chart' | 'stats' | 'enlist' | 'search' | 'none';

// Tutorial step definitions
export interface TutorialStep {
  id: string;
  title: string;
  content: string[];
  highlight: TutorialHighlight[];  // Which elements to make bright (others dimmed)
  tooltipPosition: 'top' | 'bottom' | 'left' | 'right' | 'center';
  showTwitterButton?: boolean;
}

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to MEGA.ARMY',
    content: [
      'A real-time visualizer for the MegaETH blockchain',
      'Watch every transaction flow through the network live',
      'Let\'s take a quick tour!'
    ],
    highlight: ['none'],
    tooltipPosition: 'center'
  },
  {
    id: 'circles',
    title: 'Circles = Dapps & Addresses',
    content: [
      'Each circle represents a dapp or address',
      'Size reflects activity level of transactions received',
      'Drag to reposition, positions save automatically',
      'Click any circle for detailed stats',
      'Use the search bar to add any dapp or address'
    ],
    highlight: ['circles', 'search'],
    tooltipPosition: 'center'
  },
  {
    id: 'flow',
    title: 'Transaction Flow',
    content: [
      'Dots flying from circles are live transactions, every transaction is visible',
      'They travel to the sequencer (the spinning icon)',
      'The sequencer processes them into miniblocks',
      'Miniblocks appear as bars on the chart',
      'Colors shift with each new EVM block'
    ],
    highlight: ['circles', 'sequencer', 'chart'],
    tooltipPosition: 'center'
  },
  {
    id: 'stats',
    title: 'Stats Dashboard',
    content: [
      'View network stats: tx/sec, gas/sec etc (more coming)',
      'Add, remove, resize and rearrange stat windows to anywhere on the screen',
      'Pin stats from any dapp to your dashboard'
    ],
    highlight: ['stats'],
    tooltipPosition: 'center'
  },
  {
    id: 'cta',
    title: 'Join the MEGA ARMY',
    content: [
      'Enlistment is coming!',
      'Follow @megadotarmy for updates'
    ],
    highlight: ['enlist'],
    tooltipPosition: 'center',
    showTwitterButton: true
  }
];

// State
const isActive = ref(false);
const currentStepIndex = ref(0);

// Computed
const currentStep = computed(() => TUTORIAL_STEPS[currentStepIndex.value]);
const totalSteps = computed(() => TUTORIAL_STEPS.length);
const isFirstStep = computed(() => currentStepIndex.value === 0);
const isLastStep = computed(() => currentStepIndex.value === TUTORIAL_STEPS.length - 1);
const currentHighlights = computed(() => currentStep.value?.highlight || []);

// LocalStorage key
const STORAGE_KEY = 'megaarmy_hasSeenTutorial';

/**
 * Check if user has seen the tutorial before
 */
function hasSeenTutorial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Mark tutorial as seen
 */
function markTutorialSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // localStorage not available
  }
}

/**
 * Start the tutorial
 */
function startTutorial(): void {
  currentStepIndex.value = 0;
  isActive.value = true;
}

/**
 * Go to next step
 */
function nextStep(): void {
  if (currentStepIndex.value < TUTORIAL_STEPS.length - 1) {
    currentStepIndex.value++;
  } else {
    completeTutorial();
  }
}

/**
 * Go to previous step
 */
function prevStep(): void {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value--;
  }
}

/**
 * Skip/close tutorial without completing
 */
function skipTutorial(): void {
  isActive.value = false;
  markTutorialSeen();
}

/**
 * Complete the tutorial (after last step)
 */
function completeTutorial(): void {
  isActive.value = false;
  markTutorialSeen();
}

/**
 * Reset tutorial (for testing)
 */
function resetTutorial(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage not available
  }
}

// Export as composable
export function useTutorial() {
  return {
    // State
    isActive,
    currentStepIndex,
    currentStep,
    totalSteps,
    isFirstStep,
    isLastStep,
    currentHighlights,

    // Methods
    hasSeenTutorial,
    startTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    completeTutorial,
    resetTutorial
  };
}
